import React, { FC, HTMLAttributes, useCallback, useEffect, useState } from 'react';
import { Animation } from '../../core/Animation';
import { setElementStyle } from './utils';
import { IDefaultComponentProps, IDisableAnimationProps } from '@src/models/props';
import { classnames } from '@src/utils/classnames';
import styled from './Collapse.module.scss';
import { useMergedValue } from '@src/utils/useMergedValue';

interface IProps
  extends IDefaultComponentProps,
    IDisableAnimationProps,
    Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style'> {
  /** Статус компонента */
  open?: boolean;

  /** Не будет создавать отдельную строку **/
  inline?: boolean;

  onEnter?: () => void;

  onEntering?: () => void;

  onEntered?: () => void;

  onExit?: () => void;

  onExiting?: () => void;

  onExited?: () => void;
}

export const Collapse: FC<IProps> = ({
  inline,
  disableAnimation,
  children,
  className,
  style,
  onEntered,
  onExited,
  onEnter,
  onEntering,
  onExit,
  onExiting,
  open: propsOpen,
  ...rest
}) => {
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [open, setOpen] = useState(!!propsOpen);

  const { mergedValue } = useMergedValue({ value: open, propsValue: propsOpen });

  const handleEnter = useCallback(
    (el?: HTMLElement) => {
      setOpen(true);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (typeof height === 'undefined') {
          setHeight(rect.height);
        }
        setElementStyle(el);
      }

      onEnter?.();
    },
    [height, onEnter],
  );

  const handleEntering = useCallback(
    (el?: HTMLElement) => {
      if (el) {
        setElementStyle(el, height);
      }

      onEntering?.();
    },
    [height, onEntering],
  );

  const handleEntered = useCallback(
    (el?: HTMLElement) => {
      if (el) {
        setElementStyle(el, 'none');
      }
      onEntered?.();
    },
    [onEntered],
  );

  const handleExit = useCallback(
    (el?: HTMLElement) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        setHeight(rect.height);
        setElementStyle(el, height);
      }

      onExit?.();
    },
    [height, onExit],
  );

  const handleExiting = useCallback(
    (el?: HTMLElement) => {
      if (el) {
        setElementStyle(el);
      }
      onExiting?.();
    },
    [onExiting],
  );

  const handleExited = useCallback(() => {
    setOpen(false);
    onExited?.();
  }, [onExited]);

  useEffect(() => {
    /** CEO @link https://chromestatus.com/feature/4733392803332096 */
    const isSearchHash = location.hash.includes(':~:text=');
    if (isSearchHash) {
      setOpen(true);
      return;
    }
  }, []);

  const classes = classnames(styled.root, className, {
    [styled.inline]: inline,
    [styled.rootClose]: !mergedValue,
  });

  return (
    <div className={classes} style={style} {...rest}>
      {disableAnimation ? (
        mergedValue && children
      ) : (
        <Animation
          open={mergedValue}
          onEnter={handleEnter}
          onEntering={handleEntering}
          onEntered={handleEntered}
          onExit={handleExit}
          onExiting={handleExiting}
          onExited={handleExited}
        >
          {children}
        </Animation>
      )}
    </div>
  );
};
